// Доступ к данным кабинета для экранов: клиенты, группы, типы, сессии, окна дня, подписи.
import { GROUP_KIND, MEET, RU_DAYS_FULL, STATUS, STATUS_LABEL, addDays, dowOf, freeWindows, hmToMin, isCommercial,
  minToHm, money, ruDate, tzCity, type ClientOut, type DaySession, type FreeWindow, type GroupOut, type SessionOut, type Status,
  type TypeOut, type WorkDay } from '@praktika/core';
import { cap, esc } from './dom.ts';
import { S } from './state.ts';
import { busyTimed } from './busy.ts';
import { convertTime, nowInfo } from './zones.ts';

export function clientById(id: string | null | undefined): ClientOut | null {
  return S.clients.find((c) => c.id === id) ?? null;
}
export function groupById(id: string | null | undefined): GroupOut | null {
  return S.groups.find((g) => g.id === id) ?? null;
}
export function sessionById(id: string | null | undefined): SessionOut | null {
  return S.sessions.find((x) => x.id === id) ?? null;
}
export function typeById(id: string | null | undefined): TypeOut {
  return S.types.find((t) => t.id === id) ?? defaultType();
}
export function typeName(id: string | null | undefined): string {
  return id ? typeById(id).name : '';
}
export function defaultType(): TypeOut {
  return S.types.find((t) => t.isDefault && t.active) ?? S.types[0] ??
    { id: '', name: 'Стандартная', dur: 50, brk: 15, isDefault: true, active: true, price: null, cur: null };
}
export function activeTypes(): TypeOut[] { return S.types.filter((t) => t.active); }

// коммерческие проекты (платит организация)
export function projects(): GroupOut[] { return S.groups.filter(isCommercial); }

export function defaultGroup(): GroupOut | { id: string; name: string; kind: typeof GROUP_KIND.PERSONAL } {
  return groupById(S.groupDefault) ?? { id: '', name: 'Личные', kind: GROUP_KIND.PERSONAL };
}

// группа клиента или сессии
export function groupOf(x: { groupId?: string } | null | undefined): GroupOut | null {
  return x ? groupById(x.groupId) : null;
}

export function isProjectClient(c: { groupId?: string } | null | undefined): boolean {
  return isCommercial(groupOf(c));
}

export interface Payer { id: string; name: string; cur: string; balance: number | null; isProject: boolean }

// Кто платит за клиента: его коммерческий проект или он сам.
export function payerOf(c: ClientOut): Payer | null {
  const g = groupOf(c);
  if (isCommercial(g)) return { id: g!.id, name: g!.name, cur: g!.cur || S.settings.main_currency, balance: g!.balance,
    isProject: true };
  return { id: c.id, name: c.name, cur: c.balanceCur || c.cur || S.settings.main_currency, balance: c.balance,
    isProject: false };
}

// «личный» у группы по умолчанию, иначе название группы («хПК», «Опора»)
export function groupLabel(x: { groupId?: string } | null | undefined): string {
  const g = groupOf(x);
  return g && !g.isDefault ? g.name : 'личный';
}

export function statusLabel(st: Status): string { return STATUS_LABEL[st] ?? st; }

export function statusCls(st: Status): string {
  return st === STATUS.DONE ? 'done' : st === STATUS.PLAN ? 'plan' : st === STATUS.RESERVE ? 'res' : 'off';
}

export function workOf(date: string): WorkDay | null {
  const w = S.week[dowOf(date)];
  if (!w || !w.on) return null;
  const ls = hmToMin(w.lunchStart), le = hmToMin(w.lunchEnd), lunch = ls !== null && le !== null && le > ls;
  return { on: true, start: hmToMin(w.start) ?? 0, end: hmToMin(w.end) ?? 0, lunchStart: lunch ? ls : null,
    lunchEnd: lunch ? le : null };
}

export interface DayItem extends DaySession { id: string; name: string; sess: SessionOut }

export function daySessions(date: string): DayItem[] {
  return S.sessions.filter((x) => x.date === date).map((x) => {
    const t = typeById(x.typeId);
    return { id: x.id, start: hmToMin(x.start) ?? 0, dur: Number(x.dur) || t.dur, brk: t.brk, status: x.status,
      name: x.clientName, sess: x };
  }).sort((a, b) => a.start - b.start);
}

export function windowsOf(date: string, ignoreNow = false): FreeWindow[] {
  const n = nowInfo(), o: { step: number; busy: ReturnType<typeof busyTimed>; nowMin?: number } =
    { step: S.settings.step || 5, busy: busyTimed(date) };
  if (date < n.date && !ignoreNow) return [];
  if (date === n.date && !ignoreNow) o.nowMin = n.min;
  return freeWindows(workOf(date), daySessions(date), S.types, o);
}

export function ratesNow(): Record<string, number> {
  return S.ratesNow || { AMD: 1, EUR: 418.6, RUB: 4.7, USD: 390, GEL: 140, GBP: 500 };
}

// сумма в основной валюте по сегодняшнему курсу
export function toMain(amount: number, cur: string | null | undefined): number {
  const r = ratesNow(), c = cur || S.settings.main_currency;
  if (c === S.settings.main_currency) return amount;
  return amount * (r[c] || 1);
}

export function meetLabel(x: { meet?: string; link?: string; address?: string }): string {
  if (x.meet === MEET.OFFLINE) return 'очно' + (x.address ? ', ' + x.address : '');
  if (x.meet === MEET.MEET) return 'Meet, новая';
  if (x.meet === MEET.ZOOM) return 'Zoom, новая';
  if (x.meet === MEET.OWN_LINK) return (/zoom/i.test(x.link ?? '') ? 'Zoom' : /meet\.google/i.test(x.link ?? '') ? 'Meet' :
    'ссылка') + ', своя постоянная';
  if (x.meet === MEET.BY_GROUP) return 'ссылку присылает проект';
  if (x.meet === MEET.NONE) return 'без встречи';
  return x.meet || '';
}

export function meetShort(x: { meet?: string; link?: string }): string {
  if (x.meet === MEET.OFFLINE) return 'очно';
  if (x.meet === MEET.NONE) return 'без встречи';
  if (x.link && /zoom/i.test(x.link)) return 'Zoom';
  if (x.link && /meet/i.test(x.link)) return 'Meet';
  if (x.meet === MEET.ZOOM) return 'Zoom';
  if (x.meet === MEET.MEET) return 'Meet';
  return x.meet === MEET.BY_GROUP ? 'от проекта' : (x.link ? 'ссылка' : 'онлайн');
}

export function balPill(b: number | null | undefined, cur: string | null | undefined): string {
  if (!b) return '<span class="bal zero">0</span>';
  return b < 0 ? '<span class="bal neg">долг ' + esc(money(-b, cur)) + '</span>' :
    '<span class="bal pos">аванс ' + esc(money(b, cur)) + '</span>';
}

export function dayTitle(date: string): string {
  const n = nowInfo();
  return cap(RU_DAYS_FULL[dowOf(date)] ?? '') + ', ' + ruDate(date) +
    (date === n.date ? '' : date === addDays(n.date, 1) ? ' · завтра' : '');
}

// «у Ольги 09:00 Берлин» — время сессии у клиента в другом поясе
export function clientTimeLabel(x: SessionOut, c: ClientOut | null): string {
  if (!c || !c.tz || c.tz === S.tz) return '';
  const t = convertTime(x.date, hmToMin(x.start) ?? 0, S.tz, c.tz);
  return 'у ' + (c.callName || c.name) + ' ' + minToHm(t.min) + (t.date !== x.date ? ' ' + ruDate(t.date) : '') + ' ' +
    tzCity(c.tz);
}
